import { ApiService } from '../ApiService';
import type { 
  BalanceSheetData, 
  IncomeStatementData, 
  CashFlowStatementData,
  EquityChangesData,
  NotesData,
  FinancialStatementType
} from '../../../types/reports';
import { CurrencyCode } from '../../../config/currency';

/**
 * Paramètres de base pour les endpoints GET de rapports
 * Selon la documentation, ces endpoints acceptent: date/startDate/endDate, comparative, currency
 * NOTE: companyId (organizationId) est REQUIS par le backend pour l'isolation des données
 */
export interface ReportGetParams {
  currency?: CurrencyCode;
  comparative?: boolean;
  companyId?: string; // ID de l'organisation (requis par le backend)
}

export const reportsApi = {
  /**
   * Récupérer le bilan (GET /reports/balance-sheet)
   * Query params documentés: date (required), comparative (optional), currency (optional)
   * NOTE: companyId est requis par le backend pour l'isolation des données
   */
  getBalanceSheet: (params: { 
    date: string;
    comparative?: boolean;
    currency?: CurrencyCode;
    companyId?: string;
  }) => 
    ApiService.get<BalanceSheetData>('/reports/balance-sheet', params),

  /**
   * Récupérer le compte de résultat (GET /reports/income-statement)
   * Query params documentés: startDate (required), endDate (required), comparative (optional), currency (optional)
   * NOTE: companyId est requis par le backend pour l'isolation des données
   */
  getIncomeStatement: (params: { 
    startDate: string;
    endDate: string;
    comparative?: boolean;
    currency?: CurrencyCode;
    companyId?: string;
  }) => 
    ApiService.get<IncomeStatementData>('/reports/income-statement', params),

  /**
   * Récupérer le tableau de flux de trésorerie (GET /reports/cash-flow)
   * Query params documentés: startDate (required), endDate (required), comparative (optional), currency (optional)
   * NOTE: companyId est requis par le backend pour l'isolation des données
   */
  getCashFlowStatement: (params: {
    startDate: string;
    endDate: string;
    comparative?: boolean;
    currency?: CurrencyCode;
    companyId?: string;
  }) => 
    ApiService.get<CashFlowStatementData>('/reports/cash-flow', params),

  /**
   * Récupérer le tableau de variation des capitaux propres (GET /reports/equity-changes)
   * Query params documentés: startDate (required), endDate (required), currency (optional)
   * NOTE: companyId est requis par le backend pour l'isolation des données
   */
  getEquityChanges: (params: {
    startDate: string;
    endDate: string;
    currency?: CurrencyCode;
    companyId?: string;
  }) =>
    ApiService.get<EquityChangesData>('/reports/equity-changes', params),

  /**
   * Récupérer les notes annexes (GET /reports/notes)
   * Query params documentés: date (required), type (required: balance, income, all)
   * NOTE: companyId est requis par le backend pour l'isolation des données
   */
  getNotes: (params: {
    date: string;
    type: 'balance' | 'income' | 'all';
    companyId?: string;
  }) =>
    ApiService.get<NotesData>('/reports/notes', params),

  /**
   * Générer un rapport financier (POST /reports/generate)
   * C'est l'endpoint principal pour générer tous types de rapports
   * 
   * Selon la documentation API :
   * - type (required): Type de rapport (balance, income, cashflow, etc.)
   * - format (optional): json, pdf, excel (défaut: json)
   * - date (optional): Date de fin pour le bilan
   * - startDate (optional): Date de début pour compte de résultat/flux de trésorerie
   * - endDate (optional): Date de fin pour compte de résultat/flux de trésorerie
   * - comparative (optional): Inclure données comparatives
   * - currency (optional): Code devise
   * - includeNotes (optional): Inclure les notes annexes
   * - filters (optional): Filtres additionnels
   */
  generateReport: (params: {
    type: FinancialStatementType;
    format?: 'pdf' | 'excel' | 'json';
    date?: string;
    startDate?: string;
    endDate?: string;
    comparative?: boolean;
    currency?: CurrencyCode;
    includeNotes?: boolean;
    filters?: {
      analytical_account_id?: string;
      cost_center_id?: string;
      department_id?: string;
      project_id?: string;
      business_unit_id?: string;
    };
  }) => {
    if (params.format === 'json' || !params.format) {
      // Pour JSON, retourner les données structurées
      return ApiService.post<any>('/reports/generate', params);
    } else {
      // Pour PDF/Excel, retourner un blob
      return ApiService.post<Blob>('/reports/generate', params);
    }
  },

  exportReport: (params: {
    type: FinancialStatementType;
    format: 'pdf' | 'excel';
    data: any;
  }) =>
    ApiService.post<Blob>('/reports/export', params),

  // Nouveaux endpoints pour recherche et filtres avancés
  searchReports: (params: {
    query: string;
    type?: FinancialStatementType;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }) =>
    ApiService.get<any>('/reports/search', params),

  getReportTemplates: () =>
    ApiService.get<any[]>('/reports/templates'),

  saveReportTemplate: (template: {
    name: string;
    type: FinancialStatementType;
    configuration: any;
  }) =>
    ApiService.post<any>('/reports/templates', template)
};